import { cn } from "@/utils";
import { Check, MessageSquareText } from "lucide-react";
import { useState } from "react";
import { AskQuestion } from "./harnessAskUserQuestion";

/**
 * A completed (or superseded) AskUserQuestion, as a compact rail step: the
 * question-header chips with the chosen answer, expandable to each question and
 * its options. The user's answer also appears as a normal message bubble right
 * below — this is the transcript record of what was asked. Never the raw JSON
 * card.
 */
export function HarnessAskStep({
  questions,
  answers,
}: {
  questions: AskQuestion[];
  /** header → chosen value, recovered from the answer message. */
  answers?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="px-4 py-2" data-testid="harness-ask-step">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="group flex w-full items-start gap-2 text-left outline-none"
      >
        <span className="relative z-[1] flex size-6 shrink-0 items-center justify-center bg-background text-n-80">
          <MessageSquareText className="size-[18px]" />
        </span>
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 pt-0.5">
          {questions.map((q) => {
            const value = answers?.[q.header];
            return (
              <span
                key={q.header}
                className="inline-flex items-center gap-1 rounded-full bg-n-10 px-2 py-0.5 font-sans text-xs text-n-70"
              >
                <Check className="size-3 text-lc-active" />
                <span className="text-n-60">{q.header}</span>
                {value && (
                  <>
                    <span className="text-n-30">·</span>
                    <span className="max-w-[16rem] truncate font-medium text-n-90">
                      {value}
                    </span>
                  </>
                )}
              </span>
            );
          })}
        </span>
      </button>

      {open && (
        <div className="ml-8 mt-2 space-y-2 border-l border-n-20 pl-3">
          {questions.map((q) => {
            const value = answers?.[q.header];
            return (
              <div key={q.header}>
                <div className="font-sans text-xs text-n-70 text-pretty">
                  {q.question}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {q.options.map((o) => {
                    const chosen =
                      value != null && value.split(", ").includes(o.label);
                    return (
                      <span
                        key={o.label}
                        className={cn(
                          "rounded-md px-2 py-0.5 font-sans text-[11px]",
                          chosen
                            ? "bg-rust/12 font-medium text-rust"
                            : "bg-n-05 text-n-50",
                        )}
                      >
                        {o.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
